/**
 * Floating toolbar panel for Mermaid diagram editor
 * Provides node shapes, direction controls, and undo/redo actions
 */

import type { DragEvent } from "react"
import { useCallback } from "react"

import { useTranslation } from "react-i18next"

import type { MermaidDirection, MermaidNodeShape } from "../utils"

import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Circle,
  Diamond,
  Hexagon,
  Redo,
  Square,
  Undo,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { useMermaidEditorStore } from "../stores/mermaid-editor-store"

type NodeShapeItem = {
  shape: MermaidNodeShape
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const NODE_SHAPES: NodeShapeItem[] = [
  { shape: "rectangle", label: "Rectangle", icon: Square },
  {
    shape: "rounded",
    label: "Rounded",
    icon: ({ className }) => (
      <svg
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <rect height="14" rx="4" width="18" x="3" y="5" />
      </svg>
    ),
  },
  {
    shape: "stadium",
    label: "Stadium",
    icon: ({ className }) => (
      <svg
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <rect height="10" rx="5" width="18" x="3" y="7" />
      </svg>
    ),
  },
  { shape: "circle", label: "Circle", icon: Circle },
  { shape: "rhombus", label: "Decision", icon: Diamond },
  { shape: "hexagon", label: "Hexagon", icon: Hexagon },
  {
    shape: "parallelogram",
    label: "Parallelogram",
    icon: ({ className }) => (
      <svg
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M6 5h14l-2 14H4z" />
      </svg>
    ),
  },
  {
    shape: "cylinder",
    label: "Database",
    icon: ({ className }) => (
      <svg
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <ellipse cx="12" cy="5" rx="8" ry="3" />
        <path d="M4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
      </svg>
    ),
  },
]

type DirectionOption = {
  direction: MermaidDirection
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const DIRECTION_OPTIONS: DirectionOption[] = [
  { direction: "TD", label: "Top to Bottom", icon: ArrowDown },
  { direction: "LR", label: "Left to Right", icon: ArrowRight },
  { direction: "BT", label: "Bottom to Top", icon: ArrowUp },
  { direction: "RL", label: "Right to Left", icon: ArrowLeft },
]

type MermaidToolbarProps = {
  className?: string
  readOnly?: boolean
}

export function MermaidToolbar({ className, readOnly = false }: MermaidToolbarProps) {
  const { t } = useTranslation()
  const { direction, setDirection, addNode, undo, redo, canUndo, canRedo } = useMermaidEditorStore()

  const onDragStart = useCallback((event: DragEvent, shape: MermaidNodeShape, label: string) => {
    event.dataTransfer.setData("application/mermaid-node/shape", shape)
    event.dataTransfer.setData("application/mermaid-node/label", label)
    event.dataTransfer.effectAllowed = "move"
  }, [])

  const handleAddNode = useCallback(
    (shape: MermaidNodeShape) => {
      addNode(shape)
    },
    [addNode]
  )

  return (
    <div
      className={cn(
        "bg-background/95 flex flex-col gap-3 rounded-lg border p-3 shadow-lg backdrop-blur-sm",
        className
      )}
    >
      {/* Shapes section */}
      <div className="flex flex-col gap-2">
        <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
          {t("mermaid.shapes", "Shapes")}
        </span>
        <div className="grid grid-cols-2 gap-1">
          {NODE_SHAPES.map((item) => (
            <Tooltip key={item.shape}>
              <TooltipTrigger asChild>
                <button
                  disabled={readOnly}
                  draggable={!readOnly}
                  type="button"
                  className={cn(
                    "bg-card hover:bg-accent flex size-8 cursor-grab items-center justify-center rounded-md border transition-colors",
                    readOnly && "cursor-not-allowed opacity-50"
                  )}
                  onClick={() => !readOnly && handleAddNode(item.shape)}
                  onDragStart={
                    readOnly ? undefined : (event) => onDragStart(event, item.shape, item.label)
                  }
                >
                  <item.icon className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{item.label}</p>
                <p className="text-muted-foreground text-xs">Click or drag</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>

      <Separator />

      {/* Direction section */}
      <div className="flex flex-col gap-2">
        <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
          {t("mermaid.direction", "Direction")}
        </span>
        <div className="grid grid-cols-2 gap-1">
          {DIRECTION_OPTIONS.map((option) => (
            <Tooltip key={option.direction}>
              <TooltipTrigger asChild>
                <Button
                  disabled={readOnly}
                  size="icon"
                  variant="outline"
                  className={cn(
                    "size-8",
                    direction === option.direction &&
                      "border-foreground bg-accent text-accent-foreground border"
                  )}
                  onClick={() => setDirection(option.direction)}
                >
                  <option.icon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{option.label}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </div>

      <Separator />

      {/* Actions section */}
      <div className="flex flex-col gap-2">
        <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
          {t("mermaid.actions", "Actions")}
        </span>
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-8"
                disabled={readOnly || !canUndo()}
                size="icon"
                variant="outline"
                onClick={undo}
              >
                <Undo className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{t("mermaid.undo", "Undo")} (Ctrl+Z)</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-8"
                disabled={readOnly || !canRedo()}
                size="icon"
                variant="outline"
                onClick={redo}
              >
                <Redo className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{t("mermaid.redo", "Redo")} (Ctrl+Y)</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
